"use client";

import { Bot, ChevronDown } from "lucide-react";
import type { Worker } from "@/app/components/workercard/WorkerCard";
import WorkerAvatar from "@/app/components/WorkerAvatar";
import { WorkerMarketplaceSection } from "@/app/components/WorkerMarketplaceSection";
import { WorkerMyTeamSection } from "@/app/components/WorkerMyTeamSection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";

export type ServiceSwitcherVariant = "top" | "mobile";

type ServiceSwitcherProps = {
  selectedWorker: Worker | null;
  activeTab?: "my-team" | "marketplace";
  hiredWorkerIds: string[];
  searchQuery?: string;
  isLoading?: boolean;
  ready?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (worker: Worker, source: "my-team" | "marketplace") => void;
  variant?: ServiceSwitcherVariant;
};

export function ServiceSwitcher({
  selectedWorker,
  activeTab,
  hiredWorkerIds,
  searchQuery = "",
  isLoading = false,
  ready = true,
  open,
  onOpenChange,
  onSelect,
  variant = "top",
}: ServiceSwitcherProps) {
  const label = selectedWorker?.name ?? "เลือกผู้ช่วย AI";
  const subtitle = selectedWorker
    ? selectedWorker.role || "ผู้ช่วย AI"
    : "ทีม AI ของร้าน";

  const triggerClass =
    variant === "top"
      ? "flex h-full min-w-0 max-w-[min(280px,42vw)] shrink-0 cursor-pointer items-center gap-2.5 border-r border-aera-lightgray px-4 text-left"
      : "mx-4 mt-3 flex w-[calc(100%-2rem)] cursor-pointer items-center gap-3 rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-2.5 text-left";

  const handleWorkerClick = (
    worker: Worker,
    source: "my-team" | "marketplace",
  ) => {
    onOpenChange(false);
    onSelect(worker, source);
  };

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="fw-service-switcher"
          aria-label={`เลือกบริการ: ${label}`}
          aria-expanded={open}
          title={label}
          className={triggerClass}
        >
          {ready ? (
            <>
              {selectedWorker ? (
                <WorkerAvatar
                  serviceId={selectedWorker.id}
                  name={selectedWorker.name}
                  thumbnail={selectedWorker.thumbnail}
                  size={variant === "mobile" ? 36 : 32}
                  className="shrink-0 rounded-full"
                  imageColor={selectedWorker.imageColor}
                  skipLoading
                />
              ) : (
                <span
                  className="flex shrink-0 items-center justify-center rounded-full bg-aera-offwhite text-aera-gray"
                  style={{
                    width: variant === "mobile" ? 36 : 32,
                    height: variant === "mobile" ? 36 : 32,
                  }}
                  aria-hidden
                >
                  <Bot className="h-4 w-4" />
                </span>
              )}
              <div
                className={
                  variant === "top" ? "min-w-0" : "min-w-0 flex-1"
                }
              >
                <p className="truncate text-sm font-semibold text-aera-charcoal">
                  {label}
                </p>
                <p className="truncate text-xs text-aera-gray">{subtitle}</p>
              </div>
            </>
          ) : (
            <>
              <div
                className="shrink-0 animate-pulse rounded-full bg-aera-lightgray"
                style={{
                  width: variant === "mobile" ? 36 : 32,
                  height: variant === "mobile" ? 36 : 32,
                }}
                aria-hidden
              />
              <div
                className="min-w-0"
                aria-busy="true"
                aria-label="กำลังโหลดบริการ"
              >
                <div className="h-4 w-28 animate-pulse rounded bg-aera-lightgray" />
                <div className="mt-1.5 h-3 w-20 animate-pulse rounded bg-aera-lightgray/70" />
              </div>
            </>
          )}
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-aera-gray transition-transform duration-200 ease-out ${
              open ? "rotate-180" : "rotate-0"
            }`}
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        collisionPadding={variant === "mobile" ? 16 : 12}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={[
          "!z-[300] flex flex-col overflow-hidden rounded-xl border border-aera-lightgray/80 p-3! shadow-[0_10px_30px_rgba(0,0,0,0.08)] animate-in fade-in zoom-in-95 duration-200 max-h-[min(var(--radix-dropdown-menu-content-available-height),calc(100dvh-5rem))]",
          variant === "mobile"
            ? "w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]"
            : "w-[min(320px,calc(100vw-2rem))]",
        ].join(" ")}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain">
          <WorkerMyTeamSection
            activeTab={activeTab}
            selectedWorker={selectedWorker}
            handleWorkerClick={handleWorkerClick}
            searchQuery={searchQuery}
            forceLoading={isLoading}
          />
          <WorkerMarketplaceSection
            activeTab={activeTab}
            selectedWorker={selectedWorker}
            handleWorkerClick={handleWorkerClick}
            searchQuery={searchQuery}
            hiredWorkerIds={hiredWorkerIds}
            forceLoading={isLoading}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
